import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, LogOut, AlertTriangle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminResetPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  const handleResetDatabase = async () => {
    if (confirmText !== 'CANCELLA TUTTO') {
      toast.error('Devi scrivere esattamente "CANCELLA TUTTO" per confermare');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/admin/reset-database`, {}, getAuthHeaders());
      
      toast.success('Database resettato con successo!');
      console.log('Dati cancellati:', response.data.deleted_counts);
      
      // Ricarica la pagina dopo 2 secondi
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      
    } catch (error) {
      console.error('Errore nel reset del database:', error);
      const errorMsg = error.response?.data?.detail || 'Errore nel reset del database';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50">
      {/* Header */}
      <div className="bg-white border-b border-red-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 py-3 sm:px-6 sm:py-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/')}
                className="border-slate-300"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <div className="flex flex-wrap items-center gap-3">
                <div className="bg-red-600 p-2 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Reset Database</h1>
                  <p className="text-sm text-red-600 font-medium">Zona Pericolosa - Solo Amministratori</p>
                </div>
              </div>
            </div>
            
            <Button
              variant="outline"
              onClick={onLogout}
              className="border-slate-300"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-lg border-2 border-red-300 p-8">
          <div className="flex items-center gap-3 mb-6">
            <AlertTriangle className="w-8 h-8 text-red-600" />
            <h2 className="text-xl sm:text-2xl font-bold text-red-900">⚠️ ATTENZIONE - OPERAZIONE IRREVERSIBILE</h2>
          </div>

          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-bold text-red-900 mb-3">Questa operazione cancellerà:</h3>
            <ul className="space-y-2 text-red-800">
              <li className="flex flex-wrap items-center gap-2">
                <Trash2 className="w-4 h-4" />
                <span>❌ TUTTI i clienti</span>
              </li>
              <li className="flex flex-wrap items-center gap-2">
                <Trash2 className="w-4 h-4" />
                <span>❌ TUTTI i preventivi</span>
              </li>
              <li className="flex flex-wrap items-center gap-2">
                <Trash2 className="w-4 h-4" />
                <span>❌ TUTTI gli ordini di lavoro</span>
              </li>
              <li className="flex flex-wrap items-center gap-2">
                <Trash2 className="w-4 h-4" />
                <span>❌ TUTTI i progetti</span>
              </li>
              <li className="flex flex-wrap items-center gap-2">
                <Trash2 className="w-4 h-4" />
                <span>❌ TUTTI i cantieri</span>
              </li>
              <li className="flex flex-wrap items-center gap-2">
                <Trash2 className="w-4 h-4" />
                <span>❌ TUTTE le notifiche</span>
              </li>
              <li className="flex items-center gap-2">
                <Trash2 className="w-4 h-4" />
                <span>❌ TUTTI gli utenti (tranne Salvatore Scerbo)</span>
              </li>
            </ul>
          </div>

          <div className="bg-green-50 border-2 border-green-300 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-bold text-green-900 mb-2">✅ Cosa rimarrà:</h3>
            <p className="text-green-800">Solo l'utente <strong>Salvatore Scerbo</strong> (salvatore.scerbo@mainsolution.eu) con ruolo admin</p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Per confermare, scrivi esattamente: <strong className="text-red-600">CANCELLA TUTTO</strong>
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Scrivi qui..."
              className="w-full px-4 py-3 border-2 border-red-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              disabled={loading}
            />
          </div>

          <div className="flex gap-4">
            <Button
              onClick={handleResetDatabase}
              disabled={loading || confirmText !== 'CANCELLA TUTTO'}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                'Cancellazione in corso...'
              ) : (
                <>
                  <Trash2 className="w-5 h-5 mr-2" />
                  RESET DATABASE
                </>
              )}
            </Button>
            
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="flex-1 py-6 text-lg border-2"
              disabled={loading}
            >
              Annulla
            </Button>
          </div>

          {confirmText === 'CANCELLA TUTTO' && (
            <div className="mt-4 bg-red-100 border border-red-300 rounded p-4 text-center">
              <p className="text-red-800 font-bold">⚠️ Sei sicuro? Questa azione NON può essere annullata!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminResetPage;
